import is from '@sindresorhus/is';
import type { Result } from 'true-myth';

import type { HttpFetcherWithBaseUrl } from '~/shared/http-fetcher';
import type { HttpFetcherError } from '~/shared/http-fetcher/http-fetcher';
import type { ValidatingHttpFetcherWithBaseUrl } from '~/shared/validating-http-fetcher';

import type {
    Comment,
    CreateCommentRequest,
    CreateReplyRequest,
    ResolveCommentRequest,
    UpdateReplyRequest,
} from '../../data-contract/comment';
import { commentSchema, commentsSchema } from '../../data-contract/comment';

export type CommentsFetchers = {
    readonly createComment: (params: {
        readonly architectureId: string;
        readonly body: CreateCommentRequest;
        readonly itemId: string;
    }) => Promise<Result<Comment, HttpFetcherError>>;
    readonly createReply: (params: {
        readonly architectureId: string;
        readonly body: CreateReplyRequest;
        readonly commentId: string;
        readonly itemId: string;
    }) => Promise<Result<Comment, HttpFetcherError>>;
    readonly deleteComment: (params: {
        readonly architectureId: string;
        readonly commentId: string;
        readonly itemId: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly deleteReply: (params: {
        readonly architectureId: string;
        readonly commentId: string;
        readonly itemId: string;
        readonly replyId: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly fetchComments: (params: {
        readonly architectureId: string;
        readonly itemId: string;
        readonly language?: string;
        readonly version: number;
    }) => Promise<Result<readonly Comment[], HttpFetcherError>>;
    readonly fetchReplies: (params: {
        readonly architectureId: string;
        readonly commentId: string;
        readonly itemId: string;
    }) => Promise<Result<readonly Comment[], HttpFetcherError>>;
    readonly resolveComment: (params: {
        readonly architectureId: string;
        readonly body: ResolveCommentRequest;
        readonly commentId: string;
        readonly itemId: string;
    }) => Promise<Result<Comment, HttpFetcherError>>;
    readonly updateReply: (params: {
        readonly architectureId: string;
        readonly body: UpdateReplyRequest;
        readonly commentId: string;
        readonly itemId: string;
        readonly replyId: string;
    }) => Promise<Result<Comment, HttpFetcherError>>;
};

export type CommentsFetchersDependencies = {
    readonly cobrainerApiFetcher: HttpFetcherWithBaseUrl;
    readonly validatingCobrainerApiFetcher: ValidatingHttpFetcherWithBaseUrl;
};

export const createCommentsFetchers = (dependencies: CommentsFetchersDependencies): CommentsFetchers => {
    const { cobrainerApiFetcher, validatingCobrainerApiFetcher } = dependencies;

    const fetchComments: CommentsFetchers['fetchComments'] = async ({ architectureId, itemId, language, version }) => {
        return validatingCobrainerApiFetcher.get(commentsSchema, {
            pathname: `/job-architectures/${architectureId}/items/${itemId}/comments`,
            requestOptions: {
                searchParams: is.undefined(language) ? { version } : { language, version },
            },
        });
    };

    const createComment: CommentsFetchers['createComment'] = async ({ architectureId, body, itemId }) => {
        return validatingCobrainerApiFetcher.postJson(commentSchema, {
            pathname: `/job-architectures/${architectureId}/items/${itemId}/comments`,
            payload: body,
        });
    };

    const deleteComment: CommentsFetchers['deleteComment'] = async ({ architectureId, commentId, itemId }) => {
        return cobrainerApiFetcher.delete({
            pathname: `/job-architectures/${architectureId}/items/${itemId}/comments/${commentId}`,
        });
    };

    const resolveComment: CommentsFetchers['resolveComment'] = async ({ architectureId, body, commentId, itemId }) => {
        return validatingCobrainerApiFetcher.postJson(commentSchema, {
            pathname: `/job-architectures/${architectureId}/items/${itemId}/comments/${commentId}/resolve`,
            payload: body,
        });
    };

    const fetchReplies: CommentsFetchers['fetchReplies'] = async ({ architectureId, commentId, itemId }) => {
        return validatingCobrainerApiFetcher.get(commentsSchema, {
            expectedStatusCodes: [404],
            pathname: `/job-architectures/${architectureId}/items/${itemId}/comments/${commentId}/replies`,
        });
    };

    const createReply: CommentsFetchers['createReply'] = async ({ architectureId, body, commentId, itemId }) => {
        return validatingCobrainerApiFetcher.postJson(commentSchema, {
            pathname: `/job-architectures/${architectureId}/items/${itemId}/comments/${commentId}/replies`,
            payload: body,
        });
    };

    const updateReply: CommentsFetchers['updateReply'] = async ({
        architectureId,
        body,
        commentId,
        itemId,
        replyId,
    }) => {
        return validatingCobrainerApiFetcher.patchJson(commentSchema, {
            pathname: `/job-architectures/${architectureId}/items/${itemId}/comments/${commentId}/replies/${replyId}`,
            payload: body,
        });
    };

    const deleteReply: CommentsFetchers['deleteReply'] = async ({ architectureId, commentId, itemId, replyId }) => {
        return cobrainerApiFetcher.delete({
            pathname: `/job-architectures/${architectureId}/items/${itemId}/comments/${commentId}/replies/${replyId}`,
        });
    };

    return {
        createComment,
        createReply,
        deleteComment,
        deleteReply,
        fetchComments,
        fetchReplies,
        resolveComment,
        updateReply,
    };
};
